import { helpElement } from '../../help/attrs';
import React, { useEffect, useState } from 'react';
import { CommercialPartyService } from '../../services/commercialParty.service';
import { CommercialPartyType, ICommercialParty } from '../../interfaces/commercialParty.interface';
import { fetchAllPages } from './common/fetchAllPages';
import SearchableSelect from '../common/SearchableSelect';

const fetchAllPartiesOfType = async (
    institutionId: string, type: CommercialPartyType,
): Promise<ICommercialParty[]> => fetchAllPages((page, size) =>
    CommercialPartyService.list(institutionId, { type, activeOnly: true, size, page })
);

interface Props {
    institutionId: string;
    /** Restrict options to these party types (e.g. `['BANK']` or `['SHIPPING_LINE', 'FREIGHT_FORWARDER']`). */
    partyTypes: string[];
    /** Selected party id (not the full object) -- Fix round 1: lets a caller pre-select from a bare
     * `*PartyId` on an existing document without hydrating a full `ICommercialParty` itself. */
    value: string | null;
    onChange: (value: ICommercialParty | null) => void;
    label?: string;
    disabled?: boolean;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    /** Help-mode key; lands on the picker's root element. */
    'data-help'?: string;
}

/**
 * Picks an active commercial party of one of `partyTypes` (Task 4a, spec §2, 7.4). Fetches every
 * active party of those types up front (the register is small master data, unlike the employee
 * roster) rather than server-side debounced search -- MUI's own `filterOptions` handles typeahead
 * client-side. Used to wire the issuing/advising bank pickers on `ExportLcForm` and the
 * forwarder/carrier pickers on `BillOfLadingForm`.
 *
 * Fix round 1: `value` is a party id, not a full party object, so a form loaded in edit mode can
 * pre-select the picker from the document's bare `*PartyId` field. When that id isn't among the
 * loaded options (inactive party, or one outside `partyTypes`), it's fetched individually via
 * `CommercialPartyService.getCommercialParty` and seeded into the options so it renders and stays
 * selectable.
 */
const CommercialPartyAutocomplete: React.FC<Props> = ({
    institutionId, partyTypes, value, onChange, label = 'Party', disabled, size = 'small', fullWidth = true, 'data-help': dataHelp,
}) => {
    const [options, setOptions] = useState<ICommercialParty[]>([]);
    const [selected, setSelected] = useState<ICommercialParty | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (disabled) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const results = await Promise.all(
                    partyTypes.map(t => fetchAllPartiesOfType(institutionId, t as CommercialPartyType))
                );
                if (cancelled) return;
                const merged = new Map<string, ICommercialParty>();
                results.forEach(parties => parties.forEach(p => merged.set(p.id, p)));
                setOptions(Array.from(merged.values()));
            } catch {
                if (!cancelled) setOptions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        })();
        return () => { cancelled = true; };
    }, [institutionId, partyTypes.join(','), disabled]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve `value` (a bare id) against the loaded options, falling back to an individual fetch
    // when the id isn't among them -- e.g. an edit-mode pre-selection for a party the current
    // partyTypes/activeOnly filter wouldn't otherwise return.
    useEffect(() => {
        if (!value) {
            setSelected(null);
            return;
        }
        const fromOptions = options.find(o => o.id === value);
        if (fromOptions) {
            setSelected(fromOptions);
            return;
        }
        if (selected?.id === value) return; // already resolved via a prior fetch
        // Wait for the up-front options fetch to settle before falling back to an individual
        // lookup -- otherwise every mount briefly fires a redundant getCommercialParty call for a
        // value that the options list was always going to include once it loaded.
        if (loading) return;
        let cancelled = false;
        (async () => {
            try {
                const party = await CommercialPartyService.getCommercialParty(institutionId, value);
                if (cancelled) return;
                setSelected(party);
                setOptions(prev => (prev.some(o => o.id === party.id) ? prev : [party, ...prev]));
            } catch {
                if (!cancelled) setSelected(null);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, options, institutionId, loading]);

    const mergedOptions = selected && !options.some(o => o.id === selected.id) ? [selected, ...options] : options;

    return (
        <SearchableSelect<ICommercialParty>
            options={mergedOptions}
            value={selected ? selected.id : ''}
            onChange={(_id, party) => onChange(party)}
            getOptionLabel={(o) => `${o.name} (${o.code})`}
            loading={loading}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            label={label}
            {...helpElement(dataHelp)}
            noOptionsText={loading ? 'Loading…' : 'No parties found'}
        />
    );
};

export default CommercialPartyAutocomplete;
